import type { TypedSupabaseClient } from '../client';
import type {
  IPdfTemplateRepository,
  PdfTemplateRow,
  CreatePdfTemplateInput,
  UpdatePdfTemplateInput,
  PdfTemplateDocScope,
} from '../../../interfaces/src';

function mapRow(row: Record<string, unknown>): PdfTemplateRow {
  return {
    id: row['id'] as string,
    docScope: row['doc_scope'] as PdfTemplateDocScope,
    docType: row['doc_type'] as string,
    name: row['name'] as string,
    designKey: (row['design_key'] as PdfTemplateRow['designKey']) ?? 'classic',
    options: (row['options'] as Record<string, unknown>) ?? {},
    isDefault: (row['is_default'] as boolean) ?? false,
    isActive: (row['is_active'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export class SupabasePdfTemplateRepository implements IPdfTemplateRepository {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(private supabase: any) {}

  async list(): Promise<PdfTemplateRow[]> {
    const { data, error } = await (this.supabase as TypedSupabaseClient)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('pdf_templates' as any)
      .select('*')
      .order('doc_scope', { ascending: true })
      .order('doc_type', { ascending: true })
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: true });
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (data ?? []).map((r: any) => mapRow(r));
  }

  async getById(id: string): Promise<PdfTemplateRow | null> {
    const { data, error } = await (this.supabase as TypedSupabaseClient)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('pdf_templates' as any)
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return mapRow(data as any);
  }

  async create(input: CreatePdfTemplateInput): Promise<PdfTemplateRow> {
    const { data, error } = await (this.supabase as TypedSupabaseClient)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('pdf_templates' as any)
      .insert({
        doc_scope: input.docScope,
        doc_type: input.docType,
        name: input.name,
        design_key: input.designKey,
        options: input.options ?? {},
        is_default: false,
        is_active: true,
      })
      .select()
      .single();
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return mapRow(data as any);
  }

  async update(id: string, input: UpdatePdfTemplateInput): Promise<PdfTemplateRow> {
    const payload: Record<string, unknown> = {};
    if (input.name !== undefined) payload['name'] = input.name;
    if (input.designKey !== undefined) payload['design_key'] = input.designKey;
    if (input.options !== undefined) payload['options'] = input.options;
    if (input.isActive !== undefined) payload['is_active'] = input.isActive;
    const { data, error } = await (this.supabase as TypedSupabaseClient)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('pdf_templates' as any)
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return mapRow(data as any);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (this.supabase as TypedSupabaseClient)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('pdf_templates' as any)
      .delete()
      .eq('id', id);
    if (error) throw error;
  }
}
